import React from 'react';
import { injectIntl } from "react-intl";

import BusComponent from "../../BusComponent";
import LegendLayout from "../../../enums/LegendLayout";
import VisualizationType from "../../../enums/VisualizationType";
import ShadedAreaLegend from "../../visualizationLegend/shadedAreaLegend/ShadedAreaLegend";
import BubbleLegend from "../../visualizationLegend/bubbleLegend/BubbleLegend";
import DotDensityLegend from "../../visualizationLegend/dotDensityLegend/DotDensityLegend";

/**
 * @typedef Props
 * @property {import('../../objects/MapInstance').default} mapInstance
 *
 * @typedef State
 * @property {import('../../').LayerDataGroup[]} layerLibraryMetadata
 *
 * @extends BusComponent<Props, State>
 */
class ColorPaletteVisualization extends BusComponent {
    constructor(props, context) {
        super(props, context);

        // Keep the legend minimized user choice in local storage and set the
        // last user choice as default state

        /** @type {State} */
        this.state = {
            layout: LegendLayout.SIMPLIFIED,
            schoolLegendVisible: false,
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            MAP_NEW_DATA_THEME_APPLIED: this.updateLegend,
            MAP_COLOR_PALETTES_CHANGED: this.updateLegend,
            MAP_NEW_COLOR_PALETTE_APPLIED: this.updateLegend,
            MAP_NEW_CUTPOINTS_APPLIED: this.updateLegend,
            VARIABLE_SELECTION_METADATA_INFO: this.onVariableSelectionMetadataInfo,
            MAP_LEGEND_RULE_HIGHLIGHTED: this.updateLegend,
        });

        this.getVariableMetadata();
        this.getLayerLibraryMetadata();
        this.getGeoJsonMetadata();
        this.emit('MAP_CLEAR_ALL_HIGHLIGHT_RULES');
    }

    componentDidUpdate(prevProps) {
        const { mapInstance } = this.props;

        if (prevProps.mapInstance.id !== mapInstance.id) {
            this.getVariableMetadata();
        }
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /**
     * @param {import('../../objects/LibraryData').default} layerLibraryData
     * @param {BusComponent} target
     */
    onLayerLibraryDataRetrieved = (layerLibraryData, target) => {
        if (target === this) {
            /**
             * @type {import('../../objects/LibraryData').default}
             * This is not instance of the LibraryData, but deep copy that has
             * same properties. You can't call methods on this object
             */
            const layerLibraryDataCopy = JSON.parse(JSON.stringify(layerLibraryData));
            this.setState({
                layerLibraryMetadata: layerLibraryDataCopy.groups,
            });
        }
    }

    onGeoJsonMetadataRetrieved = (geoJsonMetadata, target) => {
        if (target === this && geoJsonMetadata) {
            const { mapProperties } = geoJsonMetadata;
            this.setState({
                geoJsonLegendMetadata: mapProperties.legend,
            });
        }
    }

    onVariableSelectionMetadataInfo(eventMap) {
        const { mapInstance } = this.props;

        if (eventMap.source === this && eventMap.mapInstanceId === mapInstance.id) {
            // Sort data dictionary variables by variable selection
            // since metadata for variables is not sorted as the variable selection is.
            // We need the right order so we can create legend pages
            const variables = mapInstance.dataTheme.variableSelection.items.map(
                vsi => eventMap.dataDictionaryInfo.variables.find(
                    v => v.guid === vsi.variableGuid)
            );
            const isLayoutDotDensity = mapInstance.dataTheme.visualizationType === VisualizationType.DOT_DENSITY;
            const dataDictionaryInfo = {
                ...eventMap.dataDictionaryInfo,
                variables,
            };
            this.setState(prevState => ({
                dataDictionaryInfo,
                layout: isLayoutDotDensity ? LegendLayout.SIMPLIFIED : prevState.layout,
            }));
        }
    }

    updateLegend(eventMap) {
        const { mapInstance } = this.props;

        if (eventMap.source.id === mapInstance.id) {
            this.getVariableMetadata();
        }
    }

    render() {
        const { mapInstance } = this.props;
        const { layout, dataDictionaryInfo, layerLibraryMetadata } = this.state;
        const { dataTheme, libraryDataLayers } = mapInstance;

        if (!dataDictionaryInfo) {
            return (<div className="data-visualization-legend" />);
        }

        /** @type {import('../../../..').LayerDataGroupSHOULD_BE_REFACTORED[]} */
        const libraryGroupsInfo = libraryDataLayers
            // .filter(layersGroup => layersGroup.layers.some(layer => layer.visible))
            // We are removing this filter in order to show
            // data layers (opportunity zones and school digger even
            // if both layer group and all groups are invisible
            .map(layersGroup => {
                const groupInfo = layerLibraryMetadata.find(groupMetadata => groupMetadata.id === layersGroup.id);
                groupInfo.layers = layersGroup.layers;
                return groupInfo;
            });

        // detailed legend type
        let visualizationLegend;
        let visualizationLegendClass = 'visualization-legend';
        switch (dataTheme.visualizationType) {
            case VisualizationType.SHADED_AREA:
                visualizationLegendClass += '--shaded';
                visualizationLegend = (
                    <ShadedAreaLegend
                        mapInstance={mapInstance}
                        layout={layout}
                        dataDictionaryInfo={dataDictionaryInfo}
                        libraryLegendInfo={libraryGroupsInfo}
                    />);
                break;
            case VisualizationType.BUBBLES:
                visualizationLegendClass += '--bubbles';
                visualizationLegend = (
                    <BubbleLegend
                        mapInstance={mapInstance}
                        layout={layout}
                        libraryLegendInfo={libraryGroupsInfo}
                    />
                );
                break;
            case VisualizationType.DOT_DENSITY:
                visualizationLegendClass += '--dot-density';
                visualizationLegend = (
                    <DotDensityLegend
                        mapInstance={mapInstance}
                        layout={layout}
                    />
                );
                break;
        }

        return (
            <div className="visualization-legend__content">{visualizationLegend}</div>
        );
    }

    getGeoJsonMetadata = () => {
        this.bus.once('GEO_JSON_METADATA', this.onGeoJsonMetadataRetrieved);
        this.emit('GEO_JSON_METADATA_REQUEST', { source: this });
    }

    getLayerLibraryMetadata = () => {
        this.bus.once('LIBRARY_DATA', this.onLayerLibraryDataRetrieved);
        this.emit('LIBRARY_DATA_REQUEST', { source: this });
    }

    getVariableMetadata = () => {
        const { mapInstance } = this.props;
        this.emit('VARIABLE_SELECTION_METADATA_INFO_REQUEST', {
            source: this,
            mapInstanceId: mapInstance.id,
            dataDictionaryInfo: true,
            variableSelectionItems: mapInstance.dataTheme.variableSelection.items,
        });
    }
}
export default injectIntl(ColorPaletteVisualization);
